import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { RippleButton, WindReveal } from "@/components/Cinematic";

const field = "w-full rounded-sm border border-[#81754B]/40 bg-[#F7F1E1] px-4 py-3 text-[#3F3F2C] placeholder:text-[#81754B] focus:border-[#66371B] focus:outline-none focus:ring-1 focus:ring-[#66371B] transition-colors";
const label = "block text-sm uppercase tracking-[0.12em] text-[#3F3F2C] mb-2";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", goals: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = "Request a Call Back — seedesign"; }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please share your name, phone and email.");
      return;
    }
    // simple email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/submissions", form);
      setDone(true);
      toast.success("Thanks — we'll call you back within 48 hours.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-28 pb-24">
      <div className="mx-auto max-w-[720px] px-6 lg:px-10">
        <p className="text-sm uppercase tracking-[0.2em] text-[#66371B] mb-4">Request a Call Back</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#3F3F2C] leading-[0.95]">
          <WindReveal text="Let's Build Something Worth Remembering" />
        </h1>
        <p className="mt-6 text-lg text-[#3F3F2C]">
          Leave your name and number and we&apos;ll call you back — usually within 48 hours. NDA-friendly, always.
        </p>

        {done ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="mt-14 rounded-sm border border-[#5C6B4A]/40 bg-[#E3D8C1] p-12 text-center" data-testid="contact-success">
            <p className="signature text-4xl text-[#81754B]">Thank you, {form.name.split(" ")[0]}.</p>
            <p className="mt-4 text-lg text-[#3F3F2C]">We&apos;ve got your number and will call you back within 48 hours.</p>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-12 space-y-6" data-testid="contact-form">
            <div>
              <label className={label}>Name *</label>
              <input className={field} value={form.name} onChange={set("name")} data-testid="contact-name" placeholder="Your name" />
            </div>
            <div>
              <label className={label}>Phone number *</label>
              <input type="tel" className={field} value={form.phone} onChange={set("phone")} data-testid="contact-phone" placeholder="+91 ●●●●● ●●●●●" />
            </div>
            <div>
              <label className={label}>Email *</label>
              <input type="email" required className={field} value={form.email} onChange={set("email")} data-testid="contact-email" placeholder="you@brand.com" />
            </div>
            <div>
              <label className={label}>What&apos;s it about? <span className="normal-case text-[#81754B]">(optional)</span></label>
              <textarea rows={3} className={field} value={form.goals} onChange={set("goals")} data-testid="contact-goals" placeholder="A line about your brand or project" />
            </div>
            <RippleButton type="submit" testId="contact-submit" disabled={loading}
              className="rounded-full bg-[#66371B] px-10 py-4 text-[#F7F1E1] hover:bg-[#3F3F2C] transition-colors disabled:opacity-60">
              {loading ? "Sending…" : "Request a call back"}
            </RippleButton>
          </form>
        )}
      </div>
    </div>
  );
}
